//----------------------------------------------------------------------------------------------------------------------
// Audio Manager
//----------------------------------------------------------------------------------------------------------------------

import type { AudioBus, IStaticSoundOptions, StaticSound } from '@babylonjs/core';

// Engines
import type { AudioEngine } from '../engines/audio.ts';

// Interfaces
import type { Disposable } from '../interfaces/lifecycle.ts';
import type { LoggerInterface } from '../interfaces/logger.ts';

// Utils
import type { LoggingUtility } from '../utils/logger.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Public channel info exposed to consumers (no BabylonJS internals).
 */
export interface ChannelInfo
{
    volume : number;
    muted : boolean;
}

/**
 * Internal channel state including the BabylonJS bus reference.
 */
interface ChannelState extends ChannelInfo
{
    bus : AudioBus;
}

//----------------------------------------------------------------------------------------------------------------------

export class AudioManager implements Disposable
{
    private _audioEngine : AudioEngine;
    private _channels = new Map<string, ChannelState>();
    private _masterMuted = false;
    private _masterVolumeBeforeMute = 1;
    private _log : LoggerInterface;

    constructor(audioEngine : AudioEngine, logger : LoggingUtility)
    {
        this._audioEngine = audioEngine;
        this._log = logger.getLogger('AudioManager');
    }

    //------------------------------------------------------------------------------------------------------------------
    // Lifecycle
    //------------------------------------------------------------------------------------------------------------------

    async initialize(channels : string[]) : Promise<void>
    {
        this._log.info(`Initializing audio channels: ${ channels.join(', ') }`);

        for(const name of channels)
        {
            // eslint-disable-next-line no-await-in-loop
            const bus = await this._audioEngine.createBus(name);
            this._channels.set(name, { bus, volume: 1, muted: false });
        }

        this._log.info('Audio channels initialized.');
    }

    async $teardown() : Promise<void>
    {
        this._log.info('Tearing down audio manager...');

        for(const state of this._channels.values())
        {
            state.bus.dispose();
        }

        this._channels.clear();
        this._log.info('Audio manager torn down.');
    }

    //------------------------------------------------------------------------------------------------------------------
    // Sound Creation
    //------------------------------------------------------------------------------------------------------------------

    async createSound(
        name : string,
        url : string,
        channel ?: string,
        options ?: Partial<IStaticSoundOptions>
    ) : Promise<StaticSound>
    {
        let bus : AudioBus | undefined;

        if(channel)
        {
            const state = this._channels.get(channel);
            if(!state)
            {
                this._log.warn(`Unknown audio channel "${ channel }", sound "${ name }" will use default bus.`);
            }
            else
            {
                bus = state.bus;
            }
        }

        return this._audioEngine.createSound(name, url, bus, options);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Master Volume
    //------------------------------------------------------------------------------------------------------------------

    setMasterVolume(volume : number) : void
    {
        if(!this._masterMuted)
        {
            this._audioEngine.setMasterVolume(volume);
        }
        else
        {
            // Store for when we unmute
            this._masterVolumeBeforeMute = volume;
        }
    }

    getMasterVolume() : number
    {
        if(this._masterMuted)
        {
            return this._masterVolumeBeforeMute;
        }

        return this._audioEngine.getMasterVolume();
    }

    setMasterMuted(muted : boolean) : void
    {
        if(muted === this._masterMuted) { return; }

        this._masterMuted = muted;

        if(muted)
        {
            this._masterVolumeBeforeMute = this._audioEngine.getMasterVolume();
            this._audioEngine.setMasterVolume(0);
        }
        else
        {
            this._audioEngine.setMasterVolume(this._masterVolumeBeforeMute);
        }
    }

    isMasterMuted() : boolean
    {
        return this._masterMuted;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Channel Volume
    //------------------------------------------------------------------------------------------------------------------

    setChannelVolume(channel : string, volume : number) : void
    {
        const state = this._channels.get(channel);
        if(!state)
        {
            this._log.warn(`Unknown audio channel: ${ channel }`);
            return;
        }

        state.volume = volume;

        if(!state.muted)
        {
            this._audioEngine.setBusVolume(state.bus, volume);
        }
    }

    getChannelVolume(channel : string) : number
    {
        const state = this._channels.get(channel);
        return state?.volume ?? 1;
    }

    setChannelMuted(channel : string, muted : boolean) : void
    {
        const state = this._channels.get(channel);
        if(!state)
        {
            this._log.warn(`Unknown audio channel: ${ channel }`);
            return;
        }

        if(muted === state.muted) { return; }

        state.muted = muted;
        this._audioEngine.setBusVolume(state.bus, muted ? 0 : state.volume);
    }

    isChannelMuted(channel : string) : boolean
    {
        const state = this._channels.get(channel);
        return state?.muted ?? false;
    }

    getChannels() : string[]
    {
        return Array.from(this._channels.keys());
    }
}

//----------------------------------------------------------------------------------------------------------------------
